import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Table from '../../components/Table/index';
import { getManageUsersTableColumns } from './constants';
import {
  useApprovers,
  useUpdateUserRole,
  useUserCompaniesAllUserRoles,
  useUserCompaniesAllUsers,
  useUserCompaniesCurrentUserAdminState
} from '../../services/user.service';
import { Wrapper } from './style';
import toast from 'react-hot-toast';
import { USER_ROLES } from '../../consts/userRoles';
import { Filter } from './components/Filter/index';
import styled from 'styled-components';
import { InfoCards } from './components/InfoCards/index';
import { TbClock, TbUserCancel } from 'react-icons/tb';
import { TbUsers } from 'react-icons/tb';
import { TbCircleCheck } from 'react-icons/tb';
import Breadcrumb from '../../components/Breadcrumb/index';
import { ApproveDrawer } from './components/ApproveDrawer/index';
import { TbDots } from 'react-icons/tb';
import { Popover } from 'react-tiny-popover';
import { TbTrash } from 'react-icons/tb';
import { useDeactivateUser, useDeleteInvite } from '../../services/report.service';
import { InfoDrawer } from './components/InfoDrawer/index';
import { NewDeleteConfirmModal } from '../../components/NewDeleteConfirmModal/index';
import { TbChecks } from 'react-icons/tb';
import { DeleteConfirmModal } from '../../components/DeleteConfirmModal';

export const ManageUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpenAddApprover, setIsOpenAddApprover] = useState(false);
  const [action, setAction] = useState(null);
  const [isOpenMoreMenu, setIsOpenMoreMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectEditingState, setSelectEditingState] = useState({
    id: undefined,
    state: ''
  });
  const {
    data: projectUsers,
    isLoading,
    refetch: refetchProjectUsers
  } = useUserCompaniesAllUsers({
    queryOptions: {
      staleTime: 0,
      refetchOnMount: true
    }
  });
  const { data: userRoles } = useUserCompaniesAllUserRoles();
  const { data: approvers, refetch: refetchApprovers } = useApprovers({
    queryOptions: {
      enabled: searchParams.get('type') === 'reviewers'
    }
  });
  const deleteInvite = useDeleteInvite();
  const deactivateUser = useDeactivateUser();
  const updateUserRole = useUpdateUserRole();
  const { data: currentUserState, isLoading: currentUserStateLoading } =
    useUserCompaniesCurrentUserAdminState();
  const navigate = useNavigate();

  const handleRoleApproveButtonClick = selectedObject => {
    const { id: role_id, user_id, name } = selectedObject || {};
    const data = { user_id, role_id };
    updateUserRole.mutate(
      { data, query: data },
      {
        onSuccess: () => {
          toast.success('The role has been successfully changed');
          refetchProjectUsers();
        },
        onError: err => {
          console.log('handleRoleApproveButtonClick', err);
          toast.error("Something went wrong while changing user's role");
        }
      }
    );
  };

  const onOpenAddApprover = () => {
    setIsOpenAddApprover(true);
  };
  const onCloseAddApprover = () => {
    setIsOpenAddApprover(false);
  };

  const normalizedUserRoles = useMemo(() => {
    if (!userRoles?.length) return [];
    return userRoles.map(ur => ({ id: ur?.role_id, name: ur?.role_name }));
  }, [userRoles]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const includesSearchTerm = value =>
    String(value || '')
      .toLowerCase()
      .includes(normalizedSearchTerm);

  const filteredProjectUsers = useMemo(() => {
    if (!normalizedSearchTerm) return projectUsers || [];

    return (projectUsers || []).filter(user =>
      [user.name, user.email, user.role].some(includesSearchTerm)
    );
  }, [projectUsers, normalizedSearchTerm]);

  const filteredApprovers = useMemo(() => {
    const approverList = approvers?.approvers || [];
    if (!normalizedSearchTerm) return approverList;

    return approverList.filter(approver => {
      const workflowValues = (approver.workflows || []).flatMap(workflow => [
        workflow.workflow_name,
        ...(workflow.funds || []).map(fund => fund.fund_name || fund.name)
      ]);

      return [
        approver.first_name,
        approver.last_name,
        `${approver.first_name || ''} ${approver.last_name || ''}`,
        approver.email,
        approver.is_active ? 'active' : 'frozen',
        ...workflowValues
      ].some(includesSearchTerm);
    });
  }, [approvers?.approvers, normalizedSearchTerm]);

  const handleMoreMenuOpen = useCallback(azureObjectId => {
    setIsOpenMoreMenu(azureObjectId);
  }, []);

  useEffect(() => {
    if (
      !currentUserStateLoading &&
      currentUserState?.role &&
      currentUserState.role !== USER_ROLES.ADMIN &&
      !currentUserState?.is_super_admin
    ) {
      navigate('/');
    }
  }, [currentUserState]);

  const onChangeTab = val => {
    setSearchTerm('');
    setSearchParams(prev => {
      prev.set('type', val);
      return prev;
    });
  };

  const onClosePopover = () => {
    setIsOpenMoreMenu(false);
  };

  const onDelete = successFn => {
    deleteInvite.mutate(action.data.azure_object_id, {
      onSuccess: () => {
        toast.success('Reviewer has been deleted.');
        onChangeAction();
        refetchApprovers();
        successFn();
      }
    });
  };

  const onDeactivateUser = () => {
    deactivateUser.mutate(
      {
        id: action.data.azure_object_id
      },
      {
        onSuccess: () => {
          toast.success('Reviewer has been deactivated.');
          onChangeAction();
          refetchApprovers();
        }
      }
    );
  };

  const onChangeAction = (type, record) => {
    if (type) {
      setAction({
        type,
        data: record
      });
    } else {
      setAction(null);
    }
  };

  const handleDeleteMenuClick = (actionType, record) => {
    onChangeAction(actionType, record);
    setIsOpenMoreMenu(false);
  };

  const approverColumns = [
    {
      title: 'Reviewer Name',
      key: 'first_name',
      render: record => {
        return `${record.first_name} ${record.last_name}`;
      }
    },
    {
      title: 'Email',
      key: 'email'
    },
    {
      title: 'Project Access',
      key: 'aifm_access',
      render: record => {
        return (
          <AIFMAccessStyle>
            {record.workflows?.[0]?.workflow_name}{' '}
            {record.workflows.length > 1 && (
              <span onClick={() => onChangeAction('info', record.workflows)}>
                +{record.workflows.length - 1} more
              </span>
            )}
          </AIFMAccessStyle>
        );
      }
    },
    {
      title: 'AIF Access',
      key: 'aifm_access',
      render: record => {
        const _funds = record.workflows.map(item => item.funds).flat();
        const _accessGivenFunds = _funds.filter(item => item.access_given);
        const _accessNoGivenFunds = _funds.filter(item => !item.access_given);
        return _accessGivenFunds.length === _funds.length ? (
          'All funds'
        ) : (
          <AIFAccessStyle>
            All funds{' '}
            <span>
              except {_accessNoGivenFunds.length} {_accessNoGivenFunds.length > 1 ? 'AIFs' : 'AIF'}
            </span>
          </AIFAccessStyle>
        );
      }
    },
    {
      title: 'To Approve',
      key: 'to_approve',
      render: record => {
        const _funds = record.workflows.map(item => item.funds).flat();
        const _toApproveFunds = _funds.filter(item => !item.is_approved);
        return _toApproveFunds.length > 0 ? (
          <ToApproveStyle onClick={() => onChangeAction('info', record.workflows)}>
            {_toApproveFunds.length} reports
          </ToApproveStyle>
        ) : (
          '0 reports'
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: record => {
        return (
          <StatusStyle>
            <p className={record.is_active && 'active'}>{record.is_active ? 'Active' : 'Frozen'}</p>
            <Popover
              isOpen={!!isOpenMoreMenu && isOpenMoreMenu === record.azure_object_id}
              positions={['bottom', 'center']}
              onClickOutside={onClosePopover}
              content={
                <MenuListStyle>
                  <button
                    onClick={() => handleDeleteMenuClick('deactivate', record)}
                    className="menu-item delete"
                    style={{
                      color: '#495057'
                    }}
                  >
                    <TbUserCancel fontSize="16px" />
                    {record.is_active ? 'Deactivate Reviewer' : 'Activate Reviewer'}
                  </button>
                  <button
                    onClick={() => handleDeleteMenuClick('delete', record)}
                    className="menu-item delete"
                  >
                    <TbTrash fontSize="16px" />
                    Delete Reviewer
                  </button>
                </MenuListStyle>
              }
            >
              <button>
                <TbDots
                  onClick={() => handleMoreMenuOpen(record.azure_object_id)}
                  fontSize="20px"
                  color="#CED4DA"
                  cursor="pointer"
                />
              </button>
            </Popover>
          </StatusStyle>
        );
      }
    }
  ];

  const cards = useMemo(() => {
    if (approvers) {
      return [
        {
          label: 'Reports to Approve',
          value: approvers.aif_reports_pending + approvers.aifm_reports_pending,
          icon: <TbClock />
        },
        {
          label: 'Reports Approved',
          value: approvers.aif_reports_approved + approvers.aifm_reports_approved,
          icon: <TbCircleCheck />
        },
        {
          label: 'Completed Approvals',
          value: `${approvers.completed_approvals.toFixed(0)}%`,
          icon: <TbChecks />
        },
        {
          label: 'Approvers',
          value: approvers.total_approvers,
          icon: <TbUsers />
        }
      ];
    }

    return [];
  }, [approvers]);

  const type = searchParams.get('type');

  return (
    <ContainerStyle>
      <Breadcrumb
        withBackButton
        links={[{ name: 'Projects', value: '/project' }, { name: 'Settings' }]}
      />
      <Filter
        onOpenAddApprover={onOpenAddApprover}
        activeTab={type || 'members'}
        onChangeTab={onChangeTab}
        searchTerm={searchTerm}
        onChangeSearchTerm={setSearchTerm}
      />
      {type === 'reviewers' && <InfoCards items={cards} />}
      <Wrapper flexDirection="column">
        {(!type || type === 'members') && (
          <Table
            trClassName="settings-table-tr"
            onRowClick={() => {}}
            hasIndex={false}
            isLoading={isLoading}
            columns={getManageUsersTableColumns({
              selectEditingState,
              setSelectEditingState,
              selectOptions: normalizedUserRoles,
              onApproveButtonClick: handleRoleApproveButtonClick
            })}
            data={filteredProjectUsers}
          />
        )}
        {type === 'reviewers' && (
          <Table
            trClassName="settings-table-tr"
            onRowClick={() => {}}
            hasIndex={false}
            isLoading={isLoading}
            columns={approverColumns}
            data={filteredApprovers}
          />
        )}
      </Wrapper>
      <ApproveDrawer
        isOpen={isOpenAddApprover}
        refetch={refetchApprovers}
        onClose={onCloseAddApprover}
      />
      <InfoDrawer
        isOpen={action?.type === 'info'}
        action={action}
        onClose={() => onChangeAction()}
      />

      <DeleteConfirmModal
        title={action?.data?.is_active ? 'Deactivate Reviewer' : 'Activate Reviewer'}
        description={
          action?.data?.is_active
            ? 'Are you sure you want to deactivate this reviewer?'
            : 'Are you sure you want to activate this reviewer?'
        }
        isOpen={action?.type === 'deactivate'}
        onClose={() => onChangeAction()}
        cancelLabel="Cancel"
        onConfirm={onDeactivateUser}
        isLoading={deactivateUser.isLoading}
      />

      <NewDeleteConfirmModal
        title="Delete Reviewer"
        description="Are you sure you want to delete this reviewer?"
        isOpen={action?.type === 'delete'}
        onClose={() => onChangeAction()}
        submitLabel="Yes, Delete"
        type="delete"
        cancelLabel="Cancel"
        onConfirm={onDelete}
        isLoading={deleteInvite.isLoading}
      />
    </ContainerStyle>
  );
};

const ContainerStyle = styled.div`
  background-color: #f8f9fa;
  padding: 16px;
`;

const AIFAccessStyle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  span {
    color: #228be6;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    cursor: pointer;
  }
`;

const StatusStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  button {
    padding: 0;
    display: flex;
  }
  p {
    &.active {
      color: #40c057;
    }
    color: #adb5bd;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
`;

const ToApproveStyle = styled.div`
  color: #228be6;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  cursor: pointer;
`;

const AIFMAccessStyle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  span {
    color: #228be6;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    cursor: pointer;
  }
`;

export const MenuListStyle = styled.div`
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  background: #fff;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.12);
  padding: 16px;
  width: auto;
  max-width: 334px;

  .menu-item {
    width: 186px;
    padding: 8px 12px;
    color: #2b313d;
    font-size: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    border-radius: 4px;
    svg {
      width: 20px;
      height: 20px;
    }

    &.delete {
      color: red;
    }

    &:hover {
      background: #edf0f5;
    }
  }
`;
